import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SearchQuery } from '../models/search-query.model';
import {
  UniversityFinderFilter,
  UniversityFacetOption,
  UNIVERSITY_DEGREE_OPTIONS,
  UNIVERSITY_MAJOR_OPTIONS,
  UNIVERSITY_ACCREDITATION_OPTIONS,
  UNIVERSITY_OPTION_OPTIONS
} from '../../shared/models/university-finder-filter.model';
import { UniversityFinderFiltersService } from '../../shared/services/university-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';

/**
 * Group keys on UniversityFinderFilter that hold an array of selected tag names.
 */
type FacetGroup = 'degrees' | 'majors' | 'accreditation' | 'options';

/**
 * Filter UI for the University Finder, mirroring {@link FlyingClubFinderFilterListComponent}. Renders a
 * keyword field plus the Degrees / Majors / Accreditation / Additional facet groups (all AND logic). Reads
 * and writes the shared {@link UniversityFinderFiltersService} state and emits `filtersChange`.
 */
@Component({
  selector: 'app-university-finder-filter-list',
  templateUrl: './university-finder-filter-list.component.html',
  styleUrls: ['./university-finder-filter-list.component.scss']
})
export class UniversityFinderFilterListComponent implements OnInit, OnDestroy {
  @Input() compact: boolean;
  @Input() query: SearchQuery;
  @Output() filtersChange = new EventEmitter<void>();

  readonly degreeOptions = UNIVERSITY_DEGREE_OPTIONS;
  readonly majorOptions = UNIVERSITY_MAJOR_OPTIONS;
  readonly accreditationOptions = UNIVERSITY_ACCREDITATION_OPTIONS;
  readonly optionOptions = UNIVERSITY_OPTION_OPTIONS;

  universityFinderFilter = new UniversityFinderFilter();
  private _subscription: Subscription;

  /**
   * True when a university name has been entered but is too short to count toward a search. Mirrors the
   * `universityKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while the
   * entered name would be ignored.
   */
  get nameTooShort(): boolean {
    const trimmed = this.universityFinderFilter.name ? this.universityFinderFilter.name.trim() : '';
    return trimmed.length > 0 && trimmed.length < 3;
  }

  constructor(private _universityFinderFiltersService: UniversityFinderFiltersService,
              private _analyticsService: AnalyticsService) { }

  ngOnInit() {
    this._subscription = this._universityFinderFiltersService.universityFinderFilter.subscribe(f => {
      this.universityFinderFilter = f;
    });
    this._analyticsService.pageView('airport_landingpage_university', 'University Finder Landing Page filters');
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  isFacetSelected(group: FacetGroup, value: string): boolean {
    return this.universityFinderFilter[group].indexOf(value) !== -1;
  }

  onToggleFacet(group: FacetGroup, value: string, checked: boolean) {
    const selected = this.universityFinderFilter[group];
    const i = selected.indexOf(value);
    if (checked && i === -1) {
      selected.push(value);
    } else if (!checked && i !== -1) {
      selected.splice(i, 1);
    }
    this._emit();
  }

  onSelectAll(group: FacetGroup, options: UniversityFacetOption[]) {
    this.universityFinderFilter[group] = options.map(o => o.value);
    this._emit();
  }

  onClearAll(group: FacetGroup) {
    this.universityFinderFilter[group] = [];
    this._emit();
  }

  onNameChange() {
    this._emit();
  }

  private _emit() {
    // Keep the query's filter in sync as the user edits, so a university name entered here counts toward
    // search validation (see SearchQuery.validate) even before the query is serialized/submitted.
    if (this.query) {
      this.query.universityFinderFilter = this.universityFinderFilter;
    }
    this._universityFinderFiltersService.updateUniversityFinderFilters(this.universityFinderFilter);
    this.filtersChange.emit();
  }
}
